import { Emoji } from '@multipack/assets';
import { Spinner } from './Spinner';

type ConnectionStatus = 'connected' | 'connecting' | 'reconnecting' | 'disconnected';

interface LoadingOverlayProps {
  status?: ConnectionStatus;
  message?: string;
  visible?: boolean;
  onRetry?: () => void;
}

function getStatusMessage(status: ConnectionStatus): string {
  switch (status) {
    case 'connecting':
      return 'Connecting...';
    case 'reconnecting':
      return 'Reconnecting...';
    case 'disconnected':
      return 'Connection lost';
    default:
      return '';
  }
}

export function LoadingOverlay({ status, message, visible, onRetry }: LoadingOverlayProps) {
  // If explicit visible prop is false, don't show
  if (visible === false) return null;

  // If status is connected and no explicit visible=true, don't show
  if (status === 'connected' && visible !== true) return null;

  // If no status and no explicit visible=true, don't show
  if (!status && visible !== true) return null;

  const displayMessage = message || (status ? getStatusMessage(status) : 'Loading...');
  const isDisconnected = status === 'disconnected';

  return (
    <div className="fixed inset-0 z-50 bg-gray-900/95 backdrop-blur-sm flex items-center justify-center">
      <div className="text-center">
        {isDisconnected ? (
          // Disconnected state - show error icon
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        ) : (
          // Loading state - show spinner
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 border-4 border-gray-700 rounded-full" />
            <Spinner
              size="lg"
              color="primary"
              className="absolute inset-0 w-full h-full !border-4"
            />
          </div>
        )}
        <p className="text-lg text-white font-medium">{displayMessage}</p>
        {status === 'reconnecting' && (
          <p className="text-sm text-gray-400 mt-2">Your game progress is saved</p>
        )}
        {isDisconnected && (
          <>
            <p className="text-sm text-gray-400 mt-2">Check your internet connection</p>
            {onRetry && (
              <button
                onClick={onRetry}
                className="mt-4 px-6 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-lg font-medium transition-all active:scale-95"
              >
                Try Again
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

interface SplashScreenProps {
  appName?: string;
  showDescription?: boolean;
}

export function SplashScreen({ appName = 'MultiPack', showDescription = true }: SplashScreenProps) {
  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-gray-900 via-violet-950 to-gray-900 flex flex-col items-center justify-center overflow-hidden">
      {/* Animated background pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating game icons */}
        <div className="absolute top-[10%] left-[10%] opacity-20 animate-float-slow">
          <Emoji emoji="🎮" size={36} decorative />
        </div>
        <div className="absolute top-[20%] right-[15%] opacity-15 animate-float-medium">
          <Emoji emoji="🎯" size={30} decorative />
        </div>
        <div className="absolute bottom-[30%] left-[20%] opacity-10 animate-float-fast">
          <Emoji emoji="🎲" size={48} decorative />
        </div>
        <div className="absolute top-[60%] right-[10%] opacity-20 animate-float-slow">
          <Emoji emoji="🏆" size={36} decorative />
        </div>
        <div className="absolute bottom-[15%] left-[8%] opacity-15 animate-float-medium">
          <Emoji emoji="✨" size={30} decorative />
        </div>
        <div className="absolute top-[40%] left-[5%] opacity-10 animate-float-fast">
          <Emoji emoji="🎪" size={30} decorative />
        </div>
        <div className="absolute bottom-[40%] right-[20%] opacity-15 animate-float-slow">
          <Emoji emoji="🎭" size={36} decorative />
        </div>
        <div className="absolute top-[8%] right-[30%] opacity-10 animate-float-medium">
          <Emoji emoji="🎨" size={30} decorative />
        </div>

        {/* Gradient orbs */}
        <div className="absolute top-1/4 -left-20 w-40 h-40 bg-violet-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 -right-20 w-48 h-48 bg-fuchsia-500/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-violet-600/10 rounded-full blur-3xl" />
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center px-8 max-w-sm">
        {/* Logo */}
        <div className="w-24 h-24 mb-6 rounded-3xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-violet-600 flex items-center justify-center shadow-2xl shadow-violet-500/30 animate-bounce-gentle">
          <Emoji emoji="🎮" size={48} decorative />
        </div>

        <h1 className="text-3xl font-black text-white mb-2 tracking-tight">{appName}</h1>

        {/* Description for non-tech users */}
        {showDescription && (
          <div className="text-center mt-4 mb-6">
            <p className="text-gray-300 text-base leading-relaxed">
              Party games on the big screen!
            </p>
            <p className="text-gray-400 text-sm mt-2">Your phone is the controller</p>
          </div>
        )}

        {/* How it works - simple steps */}
        {showDescription && (
          <div className="w-full bg-white/5 backdrop-blur-sm rounded-2xl p-4 mb-6 border border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-violet-500/30 flex items-center justify-center text-sm font-bold text-violet-300">
                1
              </div>
              <span className="text-gray-300 text-sm">Look for the code on the TV</span>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-fuchsia-500/30 flex items-center justify-center text-sm font-bold text-fuchsia-300">
                2
              </div>
              <span className="text-gray-300 text-sm">Enter it here to join</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-pink-500/30 flex items-center justify-center text-sm font-bold text-pink-300">
                3
              </div>
              <span className="text-gray-300 text-sm">Play together!</span>
            </div>
          </div>
        )}

        {/* Loading indicator */}
        <div className="flex items-center gap-3 text-gray-400">
          <Spinner size="sm" color="primary" />
          <span className="text-sm">Getting ready...</span>
        </div>
      </div>
    </div>
  );
}
